import Tag from "@ui-kit/data-display/Tag/Tag.web";
import Heading from "@ui-kit/typography/Heading/Heading.web";
import QuantitySelect from "@ui-application/form/QuantitySelect/QuantitySelect.web";
import Card from "@ui-kit/data-display/Card/Card.web";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import useWindowSize from "@shared/hooks/useWindowSize";
import AllInPricing from "@shared/modules/financials/components/AllInPricing/AllInPricing.web";
import { getItemPrice } from "@shared/modules/financials/utils/price-utils";
import ItemSelectVerbiage from "@shared/modules/seating/components/ItemSelectVerbiage/ItemSelectVerbiage.web";
import breakpoints from "@shared/theme/props/breakpoints";
import { useTranslation } from "@shared/utils/internationalization";
import { useMemo } from "react";
import { useWaitlistSelect } from "./useWaitlistSelect";
import { WaitlistSelectProps } from "./WaitlistSelect.props";

const { sm } = breakpoints;

const WaitlistSelect = (props: WaitlistSelectProps) => {
  const {
    item,
    venueData,
    voucherData,
    ticketAuthArray,
    updateTicketAuthArray,
  } = props;

  const { t } = useTranslation("seating");

  const { width: windowWidth } = useWindowSize();
  const isSmallerThanSm = useMemo(
    () => Boolean(windowWidth && windowWidth < sm - 1),
    [windowWidth]
  );

  const {
    isDisabledSelector,
    showButtons,
    purchaseSourcePlatform,
    paymentType,
    purchaseLimits,
    selectWaitlistCallback,
    getMinPurchaseLimit,
    getMaxPurchaseLimit,
    getCurrentSelectedAmount,
  } = useWaitlistSelect(props);

  return (
    <Card
      key={item.id}
      p="md"
      w="full"
      border="1px solid"
      borderColor="transparent"
      rounded={{ base: "lg", sm: "lg" }}
      display="flex"
      flexDirection="column"
      gap="xs"
    >
      <Flex alignItems="center" gap="xs">
        {/* Waitlist badge */}
        <Tag
          fontSize="sm"
          fontWeight="bold"
          backgroundColor="primary.100"
          color="primary.600"
        >
          <Text fontSize="xs" textTransform="uppercase">
            {t("Waitlist", { ns: "common" })}
          </Text>
        </Tag>
      </Flex>
      <Flex w="full" direction={{ base: "column", md: "row" }}>
        <Box width="100%" maxWidth="100%">
          {/* Show item name and color */}
          <Flex width="100%" maxWidth="100%" alignItems="center">
            <Text
              mt="2xs"
              w="element.sm"
              h="element.sm"
              borderRadius="50%"
              mr="sm"
              style={{
                background: item.colorFill,
                position: "relative",
              }}
            />
            <Heading size="2xl">{item.name}</Heading>
          </Flex>

          {/* Show item-level purchase limit when the value isn't overridden */}
          {item.minimum_purchase_limit &&
            !purchaseLimits?.multiItemsAllowed && (
              <Flex mt="md">
                <Text color="secondary.400">{`*${t(
                  "Minimum {{count}} items required",
                  {
                    count: item.minimum_purchase_limit,
                  }
                )}`}</Text>
              </Flex>
            )}

          {/* Show item price + all in pricing breakdown */}
          <Flex mt="md" alignItems="center" justifyContent="space-between">
            <Flex direction="column">
              {venueData.currency && item.price && item.fees_pricing_info && (
                <Heading as="h3" size="lg">
                  <CurrencyFormat
                    value={getItemPrice(
                      item?.price?.toString(),
                      venueData.all_in_pricing_is_enabled,
                      item?.fees_pricing_info,
                      paymentType,
                      purchaseSourcePlatform
                    )}
                    displayType="text"
                    currency={venueData.currency}
                    includeSuffix
                  />
                </Heading>
              )}
              <AllInPricing
                feesPricingInfo={item?.fees_pricing_info}
                hasAllInPricing={venueData.all_in_pricing_is_enabled}
                price={item?.price?.toString()}
                currency={venueData.currency}
                paymentType={paymentType}
                purchaseSourcePlatform={purchaseSourcePlatform}
              />
            </Flex>

            {/* Show quantity selector on same line as price */}
            {!isSmallerThanSm && showButtons && (
              <>
                <Box minWidth="element.2xl" />
                <Flex
                  width="element.9xl"
                  direction="row"
                  justifyContent="flex-end"
                >
                  <QuantitySelect
                    isDisabled={isDisabledSelector}
                    incrementorProps={{
                      showIncrementor: true,
                      currentSelectedAmount: getCurrentSelectedAmount(item),
                      min: getMinPurchaseLimit(),
                      max: getMaxPurchaseLimit(),
                      callbackFunc: selectWaitlistCallback,
                    }}
                  />
                </Flex>
              </>
            )}
          </Flex>

          {/* Show quantity selector on a new line */}
          {isSmallerThanSm && showButtons && (
            <Flex
              pt="lg"
              width="100%"
              direction="row"
              justifyContent="flex-end"
            >
              <QuantitySelect
                isDisabled={isDisabledSelector}
                incrementorProps={{
                  showIncrementor: true,
                  currentSelectedAmount: getCurrentSelectedAmount(item),
                  min: getMinPurchaseLimit(),
                  max: getMaxPurchaseLimit(),
                  callbackFunc: selectWaitlistCallback,
                }}
              />
            </Flex>
          )}
        </Box>
      </Flex>
      {/* Shows verbiage for the item's payment plans, password entry form, and readmore description */}
      <ItemSelectVerbiage
        item={item}
        voucherData={voucherData}
        venueData={venueData}
        ticketAuthArray={ticketAuthArray}
        updateTicketAuthArray={updateTicketAuthArray}
      />
    </Card>
  );
};

export default WaitlistSelect;
